import {PersonIcon} from '@primer/octicons-react'
import {Fragment} from 'react'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import styles from './assignees.module.css'
import {AssignmentEventAssignee} from './AssignmentEventAssignee'
import type {UnassignedEventNode} from './issue-viewer-page-query-types'
import {RolledupAssignedEvent, type RolledupAssignedEventProps} from './RolledupAssignedEvent'
import {TimelineRow} from './row/TimelineRow'

export type UnassignedEventFragment = Pick<UnassignedEventNode, 'actor' | 'assignee' | 'createdAt' | 'databaseId'>

type UnassignedEventProps = {
  queryRef: UnassignedEventFragment
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  rollupGroup?: RolledupAssignedEventProps['rollupGroup']
}

export function UnassignedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
}: UnassignedEventProps): React.ReactElement {
  const {actor, createdAt, assignee, databaseId} = queryRef
  const highlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={PersonIcon}
    >
      <TimelineRow.Main>
        {rollupGroup ? (
          <RolledupAssignedEvent rollupGroup={rollupGroup} />
        ) : (
          <RemovedAssigneesRendering
            queryRefs={[queryRef]}
            selfAssigned={actor?.login === assignee?.login}
            rollup={false}
          />
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

export const RemovedAssigneesRendering = ({
  queryRefs,
  selfAssigned,
  rollup,
}: {
  queryRefs: UnassignedEventFragment[]
  selfAssigned: boolean
  rollup: boolean
}) => {
  if (queryRefs.length === 0) {
    return null
  }

  return (
    <>
      {!selfAssigned && `${LABELS.timeline.unassigned} `}
      {queryRefs.map((queryRef, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Fragment key={index}>
          <InternalRemovedAssigneesRendering
            queryRef={queryRef}
            rollup={rollup}
            first={index === 0}
            last={index === queryRefs.length - 1}
          />
        </Fragment>
      ))}
    </>
  )
}

const InternalRemovedAssigneesRendering = ({
  queryRef,
  rollup,
  first,
  last,
}: {
  queryRef: UnassignedEventFragment
  rollup: boolean
  first: boolean
  last: boolean
}) => {
  const {assignee, actor} = queryRef

  if (!assignee?.login) {
    return null
  }

  return (
    <div className={styles.assigneeEventContainer}>
      {actor?.login === assignee?.login && !rollup ? (
        LABELS.timeline.removedTheirAssignment
      ) : (
        <>
          {!first && !last && <div className={styles.assigneeMarginRight}>,</div>}
          {!first && last && <div className={styles.assigneeMarginHorizontal}>{LABELS.timeline.and}</div>}
          <AssignmentEventAssignee assignee={assignee} />
        </>
      )}
    </div>
  )
}
